/**
 * FileChipList — row of non-image attachment chips for the composer.
 *
 * v18 mockup ref: `.composer-attachments .file-chip` (lines ~520-540).
 * Each chip shows an icon + filename + size + optional ingestion status
 * pill + remove button. Size is human-formatted by the caller (don't
 * leak `KB / MB` formatting here; that's a util concern).
 *
 * v1 ref: `QueryInput.tsx` lines 503-543 (generic file branch of
 * `ImagePreviewStrip`).
 */

import type * as React from "react";
import { FileText, Loader2, X } from "lucide-react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";

export interface FileChip {
  /** Stable identifier — usually `content_id` from `ChatFileAttachment`. */
  key: string;
  filename: string;
  /** Human-readable size, e.g. `"218 KB"`. Caller formats. Optional: a sent
   *  message's attachment chip may not carry a size. */
  size?: string;
  /**
   * Optional ingestion lifecycle. When unset, no status pill is rendered
   * (storage-only file). Wired to the BE `ingestion_status` field.
   */
  status?: "skipped" | "pending" | "processing" | "completed" | "failed";
  /**
   * True while the file is still being uploaded / expanded server-side (e.g. a
   * .zip exploding into many member files). Shows a spinner + "Caricamento…" so
   * the user knows it's working and doesn't re-pick the same file.
   */
  uploading?: boolean;
  /** When the file was a .zip expanded server-side, the member-file count —
   *  shown as "N file" so the user sees the archive was unpacked. */
  extractedCount?: number;
}

export interface FileChipListProps {
  files: FileChip[];
  /** Remove handler. OMIT for a READ-ONLY chip list (e.g. the attachment(s)
   *  shown inside an already-sent chat message) — no remove button is drawn. */
  onRemove?: (key: string) => void;
  className?: string;
}

const STATUS_LABEL: Record<NonNullable<FileChip["status"]>, MessageDescriptor> = {
  skipped: msg`Solo storage`,
  pending: msg`In coda`,
  processing: msg`Elaborazione…`,
  completed: msg`Indicizzato`,
  failed: msg`Errore`,
};

const STATUS_TINT: Record<NonNullable<FileChip["status"]>, string> = {
  skipped: "bg-ink-1/[0.06] text-ink-3",
  pending: "bg-ando-orange-soft text-accent",
  processing: "bg-ando-orange-soft text-accent",
  completed: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
};

export const FileChipList: React.FC<FileChipListProps> = ({
  files,
  onRemove,
  className,
}) => {
  const { i18n } = useI18n();
  if (files.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5 px-2.5 pb-1", className)}>
      {files.map((f) => (
        <div
          key={f.key}
          className="group inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white/60 px-2 py-1 text-[11px]"
          title={`${f.filename} · ${f.size}`}
        >
          {f.uploading ? (
            <Loader2
              size={11}
              strokeWidth={2}
              className="animate-spin text-accent motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <FileText size={11} strokeWidth={1.75} className="text-ink-3" aria-hidden />
          )}
          <span className="max-w-[160px] truncate font-medium text-ink-1">
            {f.filename}
          </span>
          {f.extractedCount && !f.uploading ? (
            <span className="text-[10px] text-accent tabular-nums">
              {i18n._(msg`${f.extractedCount} file`)}
            </span>
          ) : f.size && !f.uploading ? (
            <span className="text-[10px] text-ink-3 tabular-nums">{f.size}</span>
          ) : null}
          {f.uploading ? (
            <span className="rounded-sm bg-ando-orange-soft px-1.5 py-px font-medium text-[9px] text-accent uppercase tracking-wide">
              {i18n._(msg`Caricamento…`)}
            </span>
          ) : f.status ? (
            <span
              className={cn(
                "rounded-sm px-1.5 py-px font-medium text-[9px] uppercase tracking-wide",
                STATUS_TINT[f.status],
              )}
            >
              {i18n._(STATUS_LABEL[f.status])}
            </span>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              aria-label={i18n._(msg`Rimuovi ${f.filename}`)}
              onClick={() => onRemove(f.key)}
              className="flex h-4 w-4 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-ink-1/[0.06] hover:text-ink-1"
            >
              <X size={10} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
};
FileChipList.displayName = "FileChipList";
